'use client';

import { Atom } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

type LoaderSize = 'sm' | 'md' | 'lg';
type LoaderState = 'loading' | 'static';
type LoaderVariant = 'default' | 'minimal';

/** Icon diameter as a fraction of the layout container (~65–70%). */
const ICON_TO_CONTAINER = 0.675;

/** Centralized color tokens: edit here to update all usages/imports. */
const COLOR_TOKENS = {
  atom: 'text-[color:color-mix(in_srgb,var(--atom-loader-color,var(--primary))_64%,hsl(var(--foreground))_36%)]',
  dots: 'text-[color:color-mix(in_srgb,var(--atom-loader-color,var(--primary))_58%,hsl(var(--foreground))_42%)]',
  electron: 'bg-[color:color-mix(in_srgb,var(--atom-loader-color,var(--primary))_62%,hsl(var(--foreground))_38%)]',
} as const;

/** Centralized geometry tokens for compact chat/streaming row. */
const minimalCfgBase = { container: 24, electron: 2.1, iconPx: 13 } as const;
const MINIMAL_SCALE = 1.05;
const minimalCfg = {
  container: Math.round(minimalCfgBase.container * MINIMAL_SCALE),
  electron: Number((minimalCfgBase.electron * MINIMAL_SCALE).toFixed(1)),
  iconPx: Math.round(minimalCfgBase.iconPx * MINIMAL_SCALE),
} as const;

export interface AtomLoaderProps {
  state?: LoaderState;
  size?: LoaderSize;
  /** `minimal` — compact chat row: theme tint, no halo / icon drop-shadow glow. */
  variant?: LoaderVariant;
  className?: string;
  label?: string;
  showDots?: boolean;
  orbitParticles?: number;
}

/**
 * `container` = layout box (base).
 * `glowScale` = glow diameter as multiple of container (~1.4–1.8).
 * `electron` = orbit dot size (px).
 */
const sizeMap: Record<LoaderSize, { container: number; glowScale: number; electron: number }> = {
  sm: { container: 42, glowScale: 1.45, electron: 3 },
  md: { container: 52, glowScale: 1.4, electron: 3.4 },
  lg: { container: 64, glowScale: 1.35, electron: 3.9 },
};

const VARIANT_STYLE: Record<
  LoaderVariant,
  {
    iconClass: string;
    electronClass: string;
    dotsClass: string;
    showHalo: boolean;
    showIconGlow: boolean;
    showElectronGlow: boolean;
    showDotGlow: boolean;
  }
> = {
  default: {
    iconClass: COLOR_TOKENS.atom,
    electronClass: COLOR_TOKENS.electron,
    dotsClass: COLOR_TOKENS.dots,
    showHalo: false,
    showIconGlow: false,
    showElectronGlow: false,
    showDotGlow: false,
  },
  minimal: {
    iconClass: COLOR_TOKENS.atom,
    electronClass: COLOR_TOKENS.electron,
    dotsClass: COLOR_TOKENS.dots,
    showHalo: false,
    showIconGlow: false,
    showElectronGlow: false,
    showDotGlow: false,
  },
};

function clampParticles(n: number): number {
  return Math.max(3, Math.min(8, Math.round(n)));
}

function AtomHaloShell({
  cfg,
  glowPx,
  showHalo,
  className,
  children,
  ...rest
}: {
  cfg: { container: number; glowScale: number; electron: number };
  glowPx: number;
  showHalo: boolean;
  className?: string;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLSpanElement>, 'children' | 'className' | 'style'>) {
  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-visible',
        className
      )}
      style={{ width: cfg.container, height: cfg.container }}
      {...rest}
    >
      {showHalo ? (
        <span
          className="atom-loader-halo pointer-events-none absolute left-1/2 top-1/2 z-0 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: glowPx, height: glowPx }}
          aria-hidden
        />
      ) : null}
      {children}
    </span>
  );
}

export function AtomLoader({
  state = 'loading',
  size = 'md',
  variant = 'default',
  className,
  label = 'Loading message',
  showDots = true,
  orbitParticles = 6,
}: AtomLoaderProps) {
  const isMinimal = variant === 'minimal';
  const styleCfg = VARIANT_STYLE[variant];
  const cfg = isMinimal
    ? { container: minimalCfg.container, glowScale: 1, electron: minimalCfg.electron }
    : sizeMap[size];
  const glowPx = Math.round(cfg.container * cfg.glowScale);
  const iconPx = isMinimal ? minimalCfg.iconPx : Math.max(16, Math.round(cfg.container * ICON_TO_CONTAINER));
  const orbitRadius = Math.max(
    isMinimal ? 5 : 8,
    Math.floor(cfg.container / 2 - cfg.electron / 2 - (isMinimal ? 2 : 3))
  );

  const iconClass = cn(styleCfg.iconClass, styleCfg.showIconGlow && 'atom-loader-icon-glow');

  if (state === 'static') {
    return (
      <span
        className={cn('inline-flex items-center justify-center', className)}
        role="img"
        aria-label="Assistant message"
      >
        <AtomHaloShell cfg={cfg} glowPx={glowPx} showHalo={styleCfg.showHalo}>
          <Atom size={iconPx} className={iconClass} />
        </AtomHaloShell>
      </span>
    );
  }

  const count = clampParticles(orbitParticles);

  const electronClass = cn(
    'absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full',
    styleCfg.electronClass,
    styleCfg.showElectronGlow &&
      'shadow-[0_0_4px_color-mix(in_srgb,var(--atom-loader-color,var(--primary))_18%,transparent)]'
  );

  return (
    <div
      className={cn('inline-flex items-center gap-2', className)}
      role="status"
      aria-live="polite"
      aria-busy
      aria-label={label}
    >
      <AtomHaloShell cfg={cfg} glowPx={glowPx} showHalo={styleCfg.showHalo} aria-hidden="true">
        <span className="atom-loader-orbit absolute left-1/2 top-1/2 z-[1] h-0 w-0">
          {Array.from({ length: count }, (_, i) => {
            const angle = (360 / count) * i;
            return (
              <span
                key={i}
                className="absolute left-0 top-0 origin-center"
                style={{ transform: `rotate(${angle}deg)` }}
              >
                <span
                  className="atom-loader-electron-swing absolute left-0 top-0 origin-center"
                  style={{ animationDelay: `${(i / Math.max(1, count)) * 0.55}s` }}
                >
                  <span
                    className={electronClass}
                    style={{ transform: `translateX(${orbitRadius}px)`, width: cfg.electron, height: cfg.electron }}
                  />
                </span>
              </span>
            );
          })}
        </span>
        <span className="atom-loader-icon-spin relative z-10 inline-flex items-center justify-center">
          <Atom size={iconPx} className={iconClass} />
        </span>
      </AtomHaloShell>

      {showDots ? (
        <span
          className={cn('inline-flex items-center gap-1', styleCfg.dotsClass)}
          aria-hidden="true"
        >
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className={cn(
                'atom-loader-dot h-1.5 w-1.5 rounded-full bg-current',
                styleCfg.showDotGlow &&
                  'shadow-[0_0_2px_color-mix(in_srgb,var(--atom-loader-color,var(--primary))_16%,transparent)]'
              )}
              style={{ animationDelay: `${i * 0.16}s` }}
            />
          ))}
        </span>
      ) : null}

      <span className="sr-only">{label}</span>
    </div>
  );
}
